import { GridLayoutItem } from '@progress/kendo-react-layout';
import { CustomgridLayoutWrapper } from 'admin/components/wrappers';
import useSeasonApi from 'admin/hooks/useSeasonApi';
import { defaultLookup } from 'admin/pages/RiderIndividual/components/RiderCreatePage/staticDatas';
import AdminDropDown from 'app/components/AdminDropDown';
import { AdminTextField } from 'app/components/AdminTextField';
import {
    FileData,
    generateUniqueId,
    MultipleFileUploadCard,
} from 'app/components/MultipleFileUploadCard';
import useCommonLookUpDatas from 'hooks/useCommonLookupData';
import React from 'react';
import { GeneralLookup, Sponsor, SponsorDetail } from 'services/openapi';
import SimpleReactValidator from 'simple-react-validator';
import styled from 'styled-components';
import { findObjectById, getLookupArray, isEmptyArray } from 'utils/common';
type Props = {
    setSponserPayload: React.Dispatch<React.SetStateAction<Sponsor>>;
    sponserPayload: Sponsor | undefined;
    simpleValidator: React.MutableRefObject<SimpleReactValidator>;

    setCategoryDeafultvalue: React.Dispatch<
        React.SetStateAction<GeneralLookup | undefined>
    >;
    categoryDeafultvalue: GeneralLookup | undefined;
    setSelectedFiles: React.Dispatch<React.SetStateAction<FileData[]>>;
    selectedFiles: FileData[];
    viewSponser: SponsorDetail | undefined;
    onFileDelete?: (fileData: FileData, index: number) => void;
};

const SponsorForm = ({
    setSponserPayload,
    sponserPayload,
    simpleValidator,
    setCategoryDeafultvalue,
    categoryDeafultvalue,
    setSelectedFiles,
    selectedFiles,
    viewSponser,
    onFileDelete,
}: Props) => {
    const {
        getAllLookupData,
        lookUps,
        loading: lookUpLoading,
        getCategoryLookupWithSeasonId,
        categoryLookUps,
    } = useCommonLookUpDatas();
    const { currentSeasonInDropDown, serviceDataList } = useSeasonApi();
    const [divisionOnelist, setDivisionOnelist] = React.useState<
        Array<GeneralLookup>
    >([]);

    // Generate A to Z split list
    const splitList = React.useMemo(() => {
        return Array.from({ length: 26 }, (_, index) => ({
            id: String.fromCharCode(65 + index),
            name: String.fromCharCode(65 + index),
        }));
    }, []);

    const onDataChangeCategory = e => {
        setCategoryDeafultvalue(e);
        setSponserPayload({
            ...sponserPayload,
            divisionClass: defaultLookup,
        });
        let categoryLookUpList = getLookupArray('CATEGORY', lookUps);
        const matchingObject = findObjectById(categoryLookUpList, e.id);
        if (matchingObject) {
            const { children } = matchingObject;
            setDivisionOnelist(children!);
        }
    };
    const onDataChangeDivisionOne = e => {
        setSponserPayload({
            ...sponserPayload,
            divisionClass: e,
        });
    };
    const onDataChangeSplit = e => {
        setSponserPayload({
            ...sponserPayload,
            split: e?.id || e?.name,
        });
    };
    React.useEffect(() => {
        getAllLookupData('CATEGORY,DIVISION');
        currentSeasonInDropDown();
    }, []);
    React.useEffect(() => {
        if (serviceDataList?.adminLiveSeason?.id) {
            getCategoryLookupWithSeasonId(serviceDataList?.adminLiveSeason?.id ?? '');
        }
    }, [serviceDataList]);
    React.useEffect(() => {
        if (viewSponser) {
            setCategoryDeafultvalue(viewSponser?.divisionClass?.category);
            let divisionClassView: GeneralLookup = {
                id: viewSponser?.divisionClass?.id as string,
                name: viewSponser?.divisionClass?.name as string,
            };
            setSponserPayload({
                ...sponserPayload,
                name: viewSponser?.sponsorName,
                divisionClass: divisionClassView,
                split: viewSponser?.split,
            });
        }
        if (viewSponser?.attachments?.length) {
            // Create FileData objects from attachments
            const attachmentFiles = viewSponser.attachments.map(attachment => {
                const fileName = attachment?.url?.split('\\').pop() || 'file';
                // Determine file type based on extension
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                let fileType = 'image/*'; // Default to image
                const fileSize = attachment?.size! * 1024; // We don't know size from URL
                // Set appropriate file type based on extension
                if (fileExtension === 'pdf') {
                    fileType = 'application/pdf';
                } else if (['doc', 'docx'].includes(fileExtension || '')) {
                    fileType = 'application/msword';
                } else if (['xls', 'xlsx'].includes(fileExtension || '')) {
                    fileType = 'application/vnd.ms-excel';
                }

                return {
                    id: attachment.id,
                    url: attachment.url,
                    previewUrl: attachment.url,
                    name: fileName,
                    size: fileSize, // We don't know size from URL
                    type: fileType,
                    uniqueId: generateUniqueId(),
                } as FileData;
            });

            // Set as selected files if we don't already have files
            if (selectedFiles.length === 0) {
                setSelectedFiles(attachmentFiles);
            }
        }
    }, [viewSponser]);
    return (
        <GridWrapper>
            <CustomgridLayoutWrapper
                rows={[{ height: 'auto' }]}
                cols={[{ width: '30%' }, { width: '30%' }, { width: '30%' }]}
                gap={{ rows: 20, cols: 30 }}
            >
                <GridLayoutItem>
                    <AdminTextField
                        label="Sponsor Name"
                        placeholder=""
                        value={sponserPayload?.name as string}
                        onChange={e => {
                            setSponserPayload({ ...sponserPayload, name: e.target.value });
                        }}
                        name="sponserName"
                        validations="required"
                        validator={simpleValidator.current}
                        className="field-style"
                    />
                </GridLayoutItem>
                <GridLayoutItem>
                    <AdminDropDown
                        label="Category "
                        placeholder=""
                        data={categoryLookUps}
                        onDataChange={onDataChangeCategory}
                        defaultValue={categoryDeafultvalue}
                        validations="required"
                        validator={simpleValidator.current}
                        className="field-style"
                        isLoader={lookUpLoading}
                    />
                </GridLayoutItem>
                <GridLayoutItem>
                    <AdminDropDown
                        label="Division/Classes"
                        placeholder=""
                        data={divisionOnelist}
                        onDataChange={onDataChangeDivisionOne}
                        defaultValue={sponserPayload?.divisionClass}
                        className="field-style"
                        disabled={isEmptyArray(divisionOnelist)}
                        validations="required"
                        validator={simpleValidator.current}
                        isLoader={lookUpLoading}
                    />
                </GridLayoutItem>
                <GridLayoutItem>
                    <AdminDropDown
                        label="Split"
                        placeholder=""
                        data={splitList}
                        onDataChange={onDataChangeSplit}
                        defaultValue={
                            sponserPayload?.split
                                ? { id: sponserPayload.split as string, name: sponserPayload.split as string }
                                : undefined
                        }
                        className="field-style"
                        disabled={isEmptyArray(splitList)}
                        isLoader={lookUpLoading}
                    />
                </GridLayoutItem>
            </CustomgridLayoutWrapper>
            <ImageWrapper>
                <MultipleFileUploadCard
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    borderColor="#adb5bd"
                    bgColor="rgba(231, 236, 255, 0.5)"
                    accept=".png,.webp,.jpeg,.jpg,.pdf,.docx"
                    label="Upload Logo/Documents"
                    onRemoveFile={
                        onFileDelete
                            ? index => onFileDelete(selectedFiles[index], index)
                            : undefined
                    }
                />
            </ImageWrapper>
        </GridWrapper>
    );
};

export default SponsorForm;
const ImageWrapper = styled.div`
  margin-top: 20px;
`;
const GridWrapper = styled.div`
  margin-bottom: 20px;
  .team-checkbox-style {
    padding-top: 30px;
    label {
      color: #a3a3a3;
    }
  }
  @media (min-width: 1899px) and (max-width: 4000px) {
    .k-grid-layout {
      gap: 20px 15px !important;
    }
    .grid-layout-sub {
      grid-template-columns: 49% 48.5% !important;
    }
  }
  @media (max-width: 991px) {
    .k-grid-layout {
      grid-template-columns: 100% !important;
    }
  }
`;
